import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

// HTML5 drag & drop kanban (E8). Optimistic status change via onMove with
// rollback handled by the parent (it re-renders from data on failure).
//
// Quick actions (PLAN_admin_portal_phase1.md §3): every card links to the
// job detail route, its client, and an in-place edit drawer. Nested
// interactive elements stop propagation so clicks never trigger the card's
// drag start.
const COLUMNS = [
  { status: 'scheduled', label: 'Scheduled', tone: 'kanban-scheduled' },
  { status: 'in_progress', label: 'In Progress', tone: 'kanban-progress' },
  { status: 'completed', label: 'Completed', tone: 'kanban-complete' },
];

export default function KanbanBoard({ jobs, clientName, onMove, onEditJob }) {
  const navigate = useNavigate();
  const [dragId, setDragId] = React.useState(null);
  const [overColumn, setOverColumn] = React.useState(null);

  const drop = (status) => {
    setOverColumn(null);
    const job = jobs.find((item) => item.id === dragId);
    setDragId(null);
    if (job && job.status !== status) onMove(job, status);
  };

  return (
    <div className="kanban-board">
      {COLUMNS.map((column) => {
        const columnJobs = jobs.filter((job) => job.status === column.status);
        return (
          <section
            key={column.status}
            className={`kanban-column ${column.tone} ${overColumn === column.status ? 'drag-over' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setOverColumn(column.status);
            }}
            onDragLeave={() => setOverColumn((current) => (current === column.status ? null : current))}
            onDrop={() => drop(column.status)}
          >
            <header className="kanban-head">
              <h3>{column.label}</h3>
              <span className="nav-badge">{columnJobs.length}</span>
            </header>
            <div className="kanban-cards">
              {columnJobs.map((job) => (
                <article
                  key={job.id}
                  className={`kanban-card ${dragId === job.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => setDragId(job.id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => navigate(`/admin/jobs/${job.id}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') navigate(`/admin/jobs/${job.id}`);
                  }}
                >
                  <strong>{job.title}</strong>
                  <Link
                    to={`/admin/clients/${job.client_id}`}
                    onClick={(event) => event.stopPropagation()}
                    className="kanban-client-link"
                  >
                    {clientName(job.client_id)}
                  </Link>
                  <small>{job.date || '—'}{job.engineer ? ` · ${job.engineer}` : ''}</small>
                  {job.photos?.length ? <small className="photo-count">{job.photos.length} photo{job.photos.length > 1 ? 's' : ''}</small> : null}
                  <div className="kanban-card-actions">
                    <button
                      type="button"
                      className="link-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onEditJob?.(job);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="link-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/admin/jobs/${job.id}`);
                      }}
                    >
                      View
                    </button>
                  </div>
                </article>
              ))}
              {!columnJobs.length && <p className="kanban-empty">Drop jobs here</p>}
            </div>
          </section>
        );
      })}
    </div>
  );
}
